/**
 * Input Sanitization Utility
 * Sanitizes user inputs to prevent SQL injection and XSS attacks
 */

/**
 * Sanitize string input
 * Removes potentially dangerous characters
 */
export const sanitizeString = (input) => {
  if (typeof input !== 'string') {
    return input
  }

  // Remove null bytes
  let sanitized = input.replace(/\0/g, '')

  // Remove control characters except newlines and tabs
  sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '')

  // Trim whitespace
  sanitized = sanitized.trim()

  return sanitized
}

/**
 * Sanitize for SQL injection prevention
 * Escapes special SQL characters
 */
export const sanitizeSQL = (input) => {
  if (typeof input !== 'string') {
    return input
  }

  // Note: Supabase uses parameterized queries, but this adds extra protection
  // Remove SQL comment patterns
  let sanitized = input.replace(/--/g, '')
  sanitized = sanitized.replace(/\/\*/g, '')
  sanitized = sanitized.replace(/\*\//g, '')

  // Remove semicolons (statement terminators)
  sanitized = sanitized.replace(/;/g, '')

  // Remove UNION, SELECT, INSERT, UPDATE, DELETE, DROP keywords (case insensitive)
  const sqlKeywords = [
    'union',
    'select',
    'insert',
    'update',
    'delete',
    'drop',
    'alter',
    'create',
    'exec',
    'execute',
    'script',
  ]

  sqlKeywords.forEach((keyword) => {
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi')
    sanitized = sanitized.replace(regex, '')
  })

  return sanitized
}

/**
 * Sanitize for XSS prevention
 * Removes HTML tags and JavaScript
 */
export const sanitizeXSS = (input) => {
  if (typeof input !== 'string') {
    return input
  }

  let sanitized = input

  // Remove script tags and content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')

  // Remove event handlers (onclick, onerror, etc.)
  sanitized = sanitized.replace(
    /\s*on\w+\s*=\s*["'][^"']*["']/gi,
    ''
  )

  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, '')

  // Remove data: protocol (can be used for XSS)
  sanitized = sanitized.replace(/data:text\/html/gi, '')

  // Remove vbscript: protocol
  sanitized = sanitized.replace(/vbscript:/gi, '')

  // Remove iframe tags
  sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')

  // Remove object tags
  sanitized = sanitized.replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')

  // Remove embed tags
  sanitized = sanitized.replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')

  return sanitized
}

/**
 * Sanitize object recursively
 */
export const sanitizeObject = (obj, options = {}) => {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeObject(item, options))
  }

  if (typeof obj === 'string') {
    let sanitized = obj

    if (options.sanitizeSQL) {
      sanitized = sanitizeSQL(sanitized)
    }

    if (options.sanitizeXSS) {
      sanitized = sanitizeXSS(sanitized)
    }

    if (options.sanitizeString) {
      sanitized = sanitizeString(sanitized)
    }

    return sanitized
  }

  if (typeof obj === 'object') {
    const sanitized = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        // Sanitize key as well
        const sanitizedKey = sanitizeString(key)
        sanitized[sanitizedKey] = sanitizeObject(obj[key], options)
      }
    }
    return sanitized
  }

  return obj
}

/**
 * Validate UUID format
 */
export const validateUUID = (uuid) => {
  if (typeof uuid !== 'string') {
    return false
  }

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(uuid)
}

/**
 * Validate email format
 */
export const validateEmail = (email) => {
  if (typeof email !== 'string') {
    return false
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate and sanitize UUID
 */
export const sanitizeUUID = (uuid) => {
  if (!validateUUID(uuid)) {
    throw new Error('Invalid UUID format')
  }
  return uuid
}

/**
 * Validate and sanitize email
 */
export const sanitizeEmail = (email) => {
  const sanitized = sanitizeString(email.toLowerCase().trim())
  if (!validateEmail(sanitized)) {
    throw new Error('Invalid email format')
  }
  return sanitized
}

/**
 * Validate numeric input
 */
export const validateNumber = (input, min = null, max = null) => {
  const num = Number(input)
  if (isNaN(num)) {
    throw new Error('Invalid number')
  }
  if (min !== null && num < min) {
    throw new Error(`Number must be at least ${min}`)
  }
  if (max !== null && num > max) {
    throw new Error(`Number must be at most ${max}`)
  }
  return num
}

/**
 * Sanitize RPC function parameters
 */
export const sanitizeRPCParams = (params) => {
  const sanitized = {}

  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const value = params[key]

      // Sanitize based on type
      if (typeof value === 'string') {
        // For UUID fields, validate UUID format
        if (key.includes('_id') || key.includes('id') || key === 'uuid') {
          sanitized[key] = sanitizeUUID(value)
        } else {
          // Sanitize string inputs
          sanitized[key] = sanitizeString(value)
        }
      } else if (Array.isArray(value)) {
        // Recursively sanitize arrays
        sanitized[key] = value.map((item) =>
          typeof item === 'string' ? sanitizeString(item) : item
        )
      } else if (typeof value === 'object' && value !== null) {
        // Recursively sanitize objects
        sanitized[key] = sanitizeObject(value, {
          sanitizeSQL: true,
          sanitizeXSS: false, // JSON objects might contain valid HTML
          sanitizeString: true,
        })
      } else {
        // Pass through other types (numbers, booleans, null)
        sanitized[key] = value
      }
    }
  }

  return sanitized
}

/**
 * Check for SQL injection patterns
 */
export const detectSQLInjection = (input) => {
  if (typeof input !== 'string') {
    return false
  }

  const sqlPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|ALTER|CREATE|EXEC|EXECUTE)\b)/i,
    /(\b(UNION|JOIN|WHERE|HAVING|GROUP BY|ORDER BY)\b)/i,
    /(--|\/\*|\*\/|;)/,
    /(\b(OR|AND)\s+\d+\s*=\s*\d+)/i,
    /(\b(OR|AND)\s+['"]\w+['"]\s*=\s*['"]\w+['"])/i,
    /(\b(OR|AND)\s+1\s*=\s*1)/i,
    /(\b(OR|AND)\s+['"]1['"]\s*=\s*['"]1['"])/i,
  ]

  return sqlPatterns.some((pattern) => pattern.test(input))
}

/**
 * Check for XSS patterns
 */
export const detectXSS = (input) => {
  if (typeof input !== 'string') {
    return false
  }

  const xssPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
    /javascript:/gi,
    /on\w+\s*=\s*["'][^"']*["']/gi,
    /<img[^>]+src[^>]*=.*javascript:/gi,
    /<body[^>]*onload/gi,
    /<svg[^>]*onload/gi,
  ]

  return xssPatterns.some((pattern) => pattern.test(input))
}

export default {
  sanitizeString,
  sanitizeSQL,
  sanitizeXSS,
  sanitizeObject,
  sanitizeRPCParams,
  validateUUID,
  validateEmail,
  sanitizeUUID,
  sanitizeEmail,
  validateNumber,
  detectSQLInjection,
  detectXSS,
}

